import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Card variant styles
 */
export type CardVariant = 'default' | 'compact' | 'featured' | 'browse';

/**
 * Article card display data
 */
export interface ArticleCardData {
  id: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  imageAlt?: string;  // Alt text for the image (for accessibility)
  publishedDate: string;
  author: {
    name: string;
    avatarUrl?: string;
  };
  url: string;
  // Taxonomy fields for filtering
  discipline?: string;
  subject?: string;
  topics?: string[];
  themes?: string[];
  contentType?: string;
  contentSeries?: string;
  products?: string[];
}

/**
 * ArticleCardComponent - Individual blog article card
 * 
 * Displays article thumbnail, title, excerpt, date, and author
 * Supports different card variants for layout flexibility
 */
@Component({
  selector: 'article-card',
  templateUrl: './article-card.component.html',
  styleUrls: ['./article-card.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class ArticleCardComponent {
  @Input() article!: ArticleCardData;
  @Input() variant: CardVariant = 'default';
  
  /**
   * Formats the published date to "Month Day, Year" format
   */
  get formattedDate(): string {
    if (!this.article.publishedDate) return '';
    
    try {
      const date = new Date(this.article.publishedDate);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return this.article.publishedDate;
      }
      
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      return this.article.publishedDate;
    }
  }
  
  /**
   * Check if title is long (for layout adjustments)
   */
  get isLongTitle(): boolean {
    return this.article.title.length > 60;
  }
  
  /**
   * Truncate excerpt to specific length
   */
  get truncatedExcerpt(): string {
    const maxLength = 150;
    if (this.article.excerpt.length <= maxLength) {
      return this.article.excerpt;
    }
    return this.article.excerpt.substring(0, maxLength) + '...';
  }
}

